import {
  Bell,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Filter,
  Loader2,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotificationRoute,
  NOTIFICATION_FILTER_OPTIONS,
  NOTIFICATION_TYPE_BADGE_COLORS,
  NOTIFICATION_TYPE_LABELS,
  timeAgo,
} from "@/constants/notifications";
import { useNotifications } from "@/context/NotificationContext";
import { notificationService } from "@/lib/services";
import type { Notification, NotificationType as NType } from "@/types";

type FilterType = "all" | NType;

const PAGE_SIZE = 20;

export function Notifications() {
  const navigate = useNavigate();
  const {
    markRead,
    markAllRead,
    deleteNotification,
    unreadCount,
    notifications: contextNotifications,
  } = useNotifications();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await notificationService.list({
        page,
        pageSize: PAGE_SIZE,
        unreadOnly: showUnreadOnly || undefined,
        type: filterType === "all" ? undefined : filterType,
      });
      setNotifications(res.data);
      setTotalPages(res.meta.totalPages);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    } finally {
      setIsLoading(false);
    }
  }, [page, filterType, showUnreadOnly]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Re-fetch when SSE pushes new notifications to the context
  // (contextNotifications changes when SSE receives a new notification)
  const prevContextLenRef = useRef(contextNotifications.length);
  useEffect(() => {
    if (contextNotifications.length !== prevContextLenRef.current) {
      prevContextLenRef.current = contextNotifications.length;
      loadNotifications();
    }
  }, [contextNotifications.length, loadNotifications]);

  const handleMarkRead = async (uuid: string) => {
    await markRead(uuid);
    setNotifications((prev) => prev.map((n) => (n.uuid === uuid ? { ...n, readAt: new Date().toISOString() } : n)));
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        readAt: n.readAt || new Date().toISOString(),
      })),
    );
  };

  const handleDelete = async (uuid: string) => {
    await deleteNotification(uuid);
    setNotifications((prev) => prev.filter((n) => n.uuid !== uuid));
  };

  const handleFilterChange = (value: FilterType) => {
    setFilterType(value);
    setPage(1);
    setShowFilterMenu(false);
  };

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-content-primary">Notifications</h1>
            <p className="text-sm text-content-tertiary mt-1">
              {(() => {
                if (unreadCount > 0) {
                  const plural = unreadCount === 1 ? "" : "s";
                  return `${unreadCount} unread notification${plural}`;
                }
                return "All caught up";
              })()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          {/* Unread toggle */}
          <button
            onClick={() => {
              setShowUnreadOnly(!showUnreadOnly);
              setPage(1);
            }}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
              showUnreadOnly
                ? "border-brand-primary bg-brand-primary-subtle text-brand-primary"
                : "border-border-secondary text-content-tertiary hover:text-content-primary hover:border-border-hover"
            }`}
          >
            Unread only
          </button>

          {/* Type filter */}
          <div className="relative">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition ${
                filterType === "all"
                  ? "border-border-secondary text-content-tertiary hover:text-content-primary hover:border-border-hover"
                  : "border-brand-secondary bg-brand-secondary-subtle text-brand-secondary"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              {filterType === "all"
                ? "All types"
                : NOTIFICATION_TYPE_LABELS[filterType as keyof typeof NOTIFICATION_TYPE_LABELS] || filterType}
            </button>

            {showFilterMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  role="presentation" // NOSONAR — decorative backdrop element
                  onClick={() => setShowFilterMenu(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowFilterMenu(false);
                    }
                  }}
                />
                <div className="absolute top-full left-0 mt-1 w-56 bg-surface-secondary border border-border-secondary rounded-lg shadow-xl z-20 overflow-hidden py-1">
                  {NOTIFICATION_FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleFilterChange(opt.value)}
                      className={`w-full text-left px-3 py-2 text-sm transition ${
                        filterType === opt.value
                          ? "bg-surface-tertiary text-content-primary"
                          : "text-content-secondary hover:bg-surface-tertiary/50"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* List */}
        {(() => {
          if (isLoading) {
            return (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 text-content-tertiary animate-spin" />
              </div>
            );
          }
          if (notifications.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center py-20">
                <Bell className="w-12 h-12 text-content-quaternary mb-3" />
                <p className="text-content-tertiary">
                  {filterType === "all" && !showUnreadOnly
                    ? "No notifications yet"
                    : "No notifications match your filters"}
                </p>
              </div>
            );
          }
          return (
            <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden divide-y divide-border-primary">
              {notifications.map((notif) => {
                const route = getNotificationRoute(notif);
                return (
                  <div // NOSONAR - conditionally interactive notification row
                    key={notif.uuid}
                    className={`group flex items-start gap-4 px-5 py-4 transition ${route ? "cursor-pointer" : ""} ${
                      notif.readAt
                        ? "hover:bg-interactive-hover"
                        : "bg-surface-secondary/40 hover:bg-surface-secondary/60"
                    }`}
                    role={route ? "button" : undefined} // NOSONAR — contains nested interactive elements
                    tabIndex={route ? 0 : undefined} // NOSONAR — conditionally interactive element
                    onClick={() => {
                      if (route) {
                        if (!notif.readAt) handleMarkRead(notif.uuid);
                        navigate(route);
                      }
                    }}
                    onKeyDown={
                      route
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (!notif.readAt) handleMarkRead(notif.uuid);
                              navigate(route);
                            }
                          }
                        : undefined
                    } // NOSONAR — inline keyboard handler for accessible navigation; extracting would lose closure context
                  >
                    {/* Unread indicator */}
                    <div className="mt-1.5 shrink-0">
                      {notif.readAt ? (
                        <div className="w-2.5 h-2.5" />
                      ) : (
                        <div className="w-2.5 h-2.5 rounded-full bg-brand-primary" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            NOTIFICATION_TYPE_BADGE_COLORS[notif.type as keyof typeof NOTIFICATION_TYPE_BADGE_COLORS] ||
                            "text-content-tertiary bg-surface-tertiary"
                          }`}
                        >
                          {NOTIFICATION_TYPE_LABELS[notif.type as keyof typeof NOTIFICATION_TYPE_LABELS] || notif.type}
                        </span>
                        <span className="text-xs text-content-quaternary">{timeAgo(notif.createdAt)}</span>
                      </div>
                      <h4 className="text-sm font-medium text-content-primary">{notif.title}</h4>
                      <p className="text-sm text-content-tertiary mt-0.5">{notif.message}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition">
                      {route && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!notif.readAt) handleMarkRead(notif.uuid);
                            navigate(route);
                          }}
                          className="p-1.5 text-content-quaternary hover:text-brand-primary rounded-md hover:bg-interactive-active transition"
                          title="Go to"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      )}
                      {!notif.readAt && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notif.uuid);
                          }}
                          className="p-1.5 text-content-quaternary hover:text-content-primary rounded-md hover:bg-interactive-active transition"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notif.uuid);
                        }}
                        className="p-1.5 text-content-quaternary hover:text-red-400 rounded-md hover:bg-interactive-active transition"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-content-tertiary">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary hover:bg-interactive-hover rounded-lg border border-border-secondary transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary hover:bg-interactive-hover rounded-lg border border-border-secondary transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
